/* eslint-disable i18next/no-literal-string */
import {Table} from '@dropbox/dig-components/table';
import {Tooltip} from '@dropbox/dig-components/tooltips';
import {Text} from '@dropbox/dig-components/typography';
import {atoms, Box, Stack} from '@dropbox/dig-foundations';
import {UIIcon} from '@dropbox/dig-icons';
import {WarningLine} from '@dropbox/dig-icons/assets';
import cx from 'classnames';
import {
  type HeadcountRollup,
  type WireframeReportPillar,
  WireframeReportRow,
  type WireframeReportSpend,
} from 'client';
import {Link} from 'components/DSYS/Link';
import {Fragment, useId} from 'react';

import {CategorySwatch} from './WireframeCharts';
import styles from './WireframeReport.module.css';
import {
  EMPTY_VALUE,
  formatHeadcount,
  formatShare,
  formatUsd,
  shareOf,
  toNumber,
  WIREFRAME_CATEGORIES,
  WIREFRAME_GEOS,
  WireframeCategory,
  WireframeGeo,
} from './wireframeReportFormat';
import {UnpricedNote} from './WireframeUnpricedNote';

// Sub-workstreams step in under their parent.
const DEPTH_INDENT_PX = 16;

const Stat = ({label, value}: {label: string; value: string}) => (
  <div>
    <Text tagName="div" size="large" isBold>
      {label}
    </Text>
    {/* DIG's title size at regular weight, like the design's figures. */}
    <div
      className={cx(
        atoms({
          fontFamily: 'Title',
          fontSize: 'Title Medium',
          lineHeight: 'Title Medium',
          fontWeight: 'Base',
          color: 'Text Base',
        }),
        styles.statValue
      )}
    >
      {value}
    </div>
  </div>
);

const Breakdown = ({
  label,
  parts,
  headcount,
  spend,
  withSwatches,
}: {
  label: string;
  parts: readonly (WireframeCategory | WireframeGeo)[];
  withSwatches?: boolean;
  headcount: HeadcountRollup;
  spend: WireframeReportSpend;
}) => {
  const total = toNumber(headcount.headcount_fte);
  return (
    <div>
      <Text tagName="div" size="large" isBold>
        {label}
      </Text>
      <dl className={styles.breakdown}>
        {parts.map((part) => {
          const value = toNumber(headcount[part.headcount]);
          return (
            <Fragment key={part.key}>
              <dt
                className={atoms({
                  display: 'flex',
                  alignItems: 'center',
                  columnGap: '6',
                  minWidth: '0',
                })}
              >
                {withSwatches && <CategorySwatch category={part as WireframeCategory} />}
                <Text size="medium" isBold>
                  {withSwatches ? part.shortLabel : part.label}
                </Text>
              </dt>
              <dd>
                <Text size="medium">{formatHeadcount(value)}</Text>
              </dd>
              <dd>
                <Text size="medium">{formatUsd(spend[part.spend])}</Text>
              </dd>
              <dd>
                <Text size="medium" color="subtle">
                  {formatShare(shareOf(value, total))}
                </Text>
              </dd>
            </Fragment>
          );
        })}
      </dl>
    </div>
  );
};

const PillarSummary = ({pillar}: {pillar: WireframeReportPillar}) => (
  <div className={styles.summaryContainer}>
    <Box
      borderColor="Border Subtle"
      borderStyle="Solid"
      borderWidth="1"
      borderRadius="Medium"
      backgroundColor="Background Subtle"
      className={styles.summaryStrip}
    >
      <Stat label="Total HC" value={formatHeadcount(pillar.headcount.headcount_fte)} />
      <Stat label="Spend" value={formatUsd(pillar.spend.total_usd)} />
      <Breakdown
        label="HC by geo"
        parts={WIREFRAME_GEOS}
        headcount={pillar.headcount}
        spend={pillar.spend}
      />
      <Breakdown
        label="HC by category"
        parts={WIREFRAME_CATEGORIES}
        headcount={pillar.headcount}
        spend={pillar.spend}
        withSwatches
      />
    </Box>
  </div>
);

const HeaderLabel = ({children}: {children: string}) => (
  <Text size="small" isBold>
    {children}
  </Text>
);

const NumberCell = ({
  value,
  groupStart,
  isBold,
}: {
  value: string;
  groupStart?: boolean;
  isBold?: boolean;
}) => (
  <Table.Cell className={cx(styles.numeric, groupStart && styles.groupStart)}>
    <Text size="small" isBold={isBold} color={value === EMPTY_VALUE ? 'subtle' : 'standard'}>
      {value}
    </Text>
  </Table.Cell>
);

const SpendCell = ({row, isBold}: {row: WireframeReportRow; isBold?: boolean}) => {
  const unpriced = toNumber(row.spend.unpriced_headcount_fte);
  const spend = (
    <Text size="small" isBold={isBold}>
      {row.source === WireframeReportRow.source.NONE ? EMPTY_VALUE : formatUsd(row.spend.total_usd)}
    </Text>
  );
  return (
    <Table.Cell className={cx(styles.numeric, styles.groupStart)}>
      {unpriced > 0 ? (
        <Tooltip
          placement="left"
          title={`Excludes ${formatHeadcount(unpriced)} HC with no cost profile`}
        >
          <Box
            tagName="span"
            display="inline-flex"
            alignItems="center"
            columnGap="4"
            tabIndex={0}
            aria-label={`${formatUsd(row.spend.total_usd)}, excludes ${formatHeadcount(
              unpriced
            )} HC with no cost profile`}
          >
            <UIIcon
              src={WarningLine}
              size="small"
              className={atoms({color: 'Warning Base'})}
              aria-hidden
            />
            {spend}
          </Box>
        </Tooltip>
      ) : (
        spend
      )}
    </Table.Cell>
  );
};

const HierarchyRow = ({
  row,
  hrefForWorkstream,
}: {
  row: WireframeReportRow;
  hrefForWorkstream: (id: number) => string;
}) => {
  const empty = row.source === WireframeReportRow.source.NONE;
  const headcount = (value: string) => (empty ? EMPTY_VALUE : formatHeadcount(value));
  const topLevel = row.depth === 1;
  const dris = row.dris.map((person) => person.name ?? person.ldap).filter(Boolean);
  return (
    <Table.Row className={cx(topLevel && styles.topLevelRow)}>
      <Table.Cell className={styles.workstreamCell}>
        <Box style={{paddingLeft: (row.depth - 1) * DEPTH_INDENT_PX}}>
          <Link
            to={hrefForWorkstream(row.workstream_id)}
            hasNoUnderline
            showUnderlineOnHover
            monochromatic
          >
            <Text size="small" isBold={topLevel} className={styles.workstreamName} title={row.name}>
              {row.name}
            </Text>
          </Link>
        </Box>
      </Table.Cell>
      <Table.Cell>
        <Text size="small" color="subtle" className={styles.nowrap} title={dris.join(', ')}>
          {dris.length === 0
            ? EMPTY_VALUE
            : dris.length === 1
              ? dris[0]
              : `${dris[0]} +${dris.length - 1}`}
        </Text>
      </Table.Cell>
      <NumberCell isBold={topLevel} value={headcount(row.headcount.headcount_fte)} groupStart />
      <NumberCell
        isBold={topLevel}
        value={empty ? EMPTY_VALUE : formatShare(row.pillar_share)}
        groupStart
      />
      <NumberCell isBold={topLevel} value={empty ? EMPTY_VALUE : formatShare(row.company_share)} />
      {WIREFRAME_CATEGORIES.map((category, index) => (
        <NumberCell
          isBold={topLevel}
          key={category.key}
          value={headcount(row.headcount[category.headcount])}
          groupStart={index === 0}
        />
      ))}
      {WIREFRAME_GEOS.map((geo, index) => (
        <NumberCell
          isBold={topLevel}
          key={geo.key}
          value={headcount(row.headcount[geo.headcount])}
          groupStart={index === 0}
        />
      ))}
      <SpendCell row={row} isBold={topLevel} />
    </Table.Row>
  );
};

const HierarchyTable = ({
  pillar,
  hrefForWorkstream,
}: {
  pillar: WireframeReportPillar;
  hrefForWorkstream: (id: number) => string;
}) => {
  // The pillar's own row is the summary above.
  const rows = pillar.rows.filter((row) => row.depth > 0);
  if (rows.length === 0) {
    return (
      <Text size="small" color="subtle">
        This pillar has no workstreams in this half.
      </Text>
    );
  }
  return (
    // The table is its own bordered box, like the design.
    <Box
      overflowX="auto"
      borderColor="Border Subtle"
      borderStyle="Solid"
      borderWidth="1"
      borderRadius="Medium"
      className={styles.tableFrame}
    >
      <Table isNative spacing="small" hasDividers className={styles.hierarchyTable}>
        <Table.Header>
          <Table.Row isHeader>
            <Table.HeaderCell colSpan={2} className={styles.groupHeader}>
              <Text size="small" color="subtle">
                Workstream details
              </Text>
            </Table.HeaderCell>
            <Table.HeaderCell className={cx(styles.numeric, styles.groupStart)}>
              <Text size="small" color="subtle">
                Total
              </Text>
            </Table.HeaderCell>
            <Table.HeaderCell colSpan={2} className={cx(styles.groupHeader, styles.groupStart)}>
              <Text size="small" color="subtle">
                % of totals
              </Text>
            </Table.HeaderCell>
            <Table.HeaderCell colSpan={3} className={cx(styles.groupHeader, styles.groupStart)}>
              <Text size="small" color="subtle">
                HC by category
              </Text>
            </Table.HeaderCell>
            <Table.HeaderCell colSpan={3} className={cx(styles.groupHeader, styles.groupStart)}>
              <Text size="small" color="subtle">
                HC by geo
              </Text>
            </Table.HeaderCell>
            <Table.HeaderCell className={cx(styles.numeric, styles.groupStart)}>
              <Text size="small" color="subtle">
                Half-year
              </Text>
            </Table.HeaderCell>
          </Table.Row>
          <Table.Row isHeader>
            <Table.HeaderCell className={styles.workstreamCell}>
              <HeaderLabel>Workstream</HeaderLabel>
            </Table.HeaderCell>
            <Table.HeaderCell>
              <HeaderLabel>DRI</HeaderLabel>
            </Table.HeaderCell>
            <Table.HeaderCell className={cx(styles.numeric, styles.groupStart)}>
              <HeaderLabel>HC</HeaderLabel>
            </Table.HeaderCell>
            <Table.HeaderCell className={cx(styles.numeric, styles.groupStart)}>
              <HeaderLabel>Pillar</HeaderLabel>
            </Table.HeaderCell>
            <Table.HeaderCell className={styles.numeric}>
              <HeaderLabel>Company</HeaderLabel>
            </Table.HeaderCell>
            {WIREFRAME_CATEGORIES.map((category, index) => (
              <Table.HeaderCell
                key={category.key}
                className={cx(styles.numeric, index === 0 && styles.groupStart)}
              >
                <HeaderLabel>{category.shortLabel}</HeaderLabel>
              </Table.HeaderCell>
            ))}
            {WIREFRAME_GEOS.map((geo, index) => (
              <Table.HeaderCell
                key={geo.key}
                className={cx(styles.numeric, index === 0 && styles.groupStart)}
              >
                <HeaderLabel>{geo.shortLabel}</HeaderLabel>
              </Table.HeaderCell>
            ))}
            <Table.HeaderCell className={cx(styles.numeric, styles.groupStart)}>
              <HeaderLabel>Spend</HeaderLabel>
            </Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {rows.map((row, index) => (
            // A shared workstream can appear under two parents in one pillar.
            <HierarchyRow
              key={`${row.parent_workstream_id}:${row.workstream_id}:${index}`}
              row={row}
              hrefForWorkstream={hrefForWorkstream}
            />
          ))}
        </Table.Body>
      </Table>
    </Box>
  );
};

/** One pillar: its totals and breakdowns, then every workstream in its hierarchy. */
export const WireframePillarView = ({
  pillar,
  timeframeLabel,
  hrefForWorkstream,
}: {
  pillar: WireframeReportPillar;
  timeframeLabel: string;
  hrefForWorkstream: (id: number) => string;
}) => {
  const unpriced = toNumber(pillar.spend.unpriced_headcount_fte);
  const workstreamsTitleId = useId();
  return (
    <Stack gap="24">
      <Stack gap="4">
        <Text tagName="h2" size="xlarge" isBold>
          {pillar.name}
        </Text>
        <Text size="small" color="subtle">
          Costs are estimates based on headcount times cost per employee for each group ·{' '}
          {timeframeLabel}
        </Text>
      </Stack>
      <PillarSummary pillar={pillar} />
      {unpriced > 0 && <UnpricedNote fte={unpriced} />}
      <section aria-labelledby={workstreamsTitleId}>
        <Stack gap="12">
          <Text id={workstreamsTitleId} tagName="h3" size="large" isBold>
            Workstreams
          </Text>
          <HierarchyTable pillar={pillar} hrefForWorkstream={hrefForWorkstream} />
        </Stack>
      </section>
    </Stack>
  );
};
